import { DailyLimitPhrase } from "@/components/features/Credits/DailyLimitPhrase";
import { Button } from "@/design-system/Button/Button";
import { Check } from "@/design-system/Check/Check";
import { Trans, useTranslation } from "@repo/shared/i18n";

type Props = {
  /** The daily grant, per this visitor's cohort. Absent before they have one. */
  dailyLimit?: number;
  onGetStarted: () => void;
};

/**
 * The free tier, as a plan rather than a footnote.
 *
 * Built as the same card as Ultimate — same width, radius, padding and split —
 * so the two read as one system, and stacked underneath rather than beside it,
 * which keeps Ultimate the panel in the middle of the page. Everything about it
 * is quieter: a neutral border in place of the blue one, no gradient, and a
 * secondary button, so it answers "where do I start?" without competing for the
 * page's one call to action.
 *
 * It is shorter for an honest reason, not a designed one: the free tier has two
 * things to say and Ultimate has seven.
 */
export const FreePlan = ({ dailyLimit, onGetStarted }: Props) => {
  const { t } = useTranslation(["subscription", "common"]);
  return (
    <section className="mx-auto w-full max-w-[860px] overflow-hidden rounded-2xl border border-neutral-700/70 bg-panel-base">
      <div className="grid gap-8 p-7 mobile:grid-cols-[minmax(0,260px)_minmax(0,1fr)] mobile:gap-10 mobile:p-9">
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold text-white">
            {t("common:free")}
          </h2>

          <p className="mt-2.5 text-xs text-dark-tertiary">
            {t("freePlan.noCard")}
          </p>

          <Button
            variant="secondary"
            size="large"
            stretch
            className="mt-6"
            // Stopped here so the login modal this opens isn't closed by its
            // own opening click — see the same guard in UltimatePlan.
            onClick={(e) => {
              e.stopPropagation();
              onGetStarted();
            }}
          >
            {t("common:getStarted")}
          </Button>
        </div>

        <ul className="flex flex-col justify-center gap-3.5">
          <li className="flex items-start gap-2.5">
            <Check />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold leading-tight text-white">
                {/*
                  Opens the bullet, so "free AI credits" becomes "Free AI
                  credits". A digit-led "5 free AI credits" is left alone — see
                  dailyLimitNoun. Was ::first-letter, which never fired: that
                  pseudo-element only applies to block-level boxes, and this is
                  an inline span.
                */}
                <Trans
                  ns="subscription"
                  i18nKey="freePlan.dailyGrant"
                  components={{
                    phrase: (
                      <DailyLimitPhrase limit={dailyLimit} sentenceStart />
                    ),
                  }}
                />
              </span>
              <span className="text-xs leading-snug text-foreground-muted">
                {t("freePlan.dailyInfo")}
              </span>
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold leading-tight text-white">
                {t("freePlan.clubBullet.title")}
              </span>
              <span className="text-xs leading-snug text-foreground-muted">
                {t("freePlan.clubBullet.info")}
              </span>
            </span>
          </li>
        </ul>
      </div>
    </section>
  );
};
